import React, { useState, useEffect, useCallback } from 'react';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, PageHeader, Button, Modal, ConfirmModal, FormInput, FormSelect, StandardPageLayout } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import RolesPermissionsMatrix from '../components/RolesPermissionsMatrix.jsx';
import { notify } from '@/components/core';

// --- Role Form (Preserved from V1) ---
const RoleForm = ({ role, onSave, onCancel, currentUser }) => {
    const adminRoleId = currentUser?.roleId?.toLowerCase?.() || '';
    const adminScope = currentUser?.roleScope || '';
    
    const allowedScopes = [];
    if (['r1', 'r2', 'r8', 'r9', 'r10'].includes(adminRoleId)) {
        allowedScopes.push('Platform', 'Partner', 'Tenant');
    } else if (adminScope === 'Partner' || adminRoleId === 'r3') {
        allowedScopes.push('Partner', 'Tenant');
    } else {
        allowedScopes.push('Tenant');
    }

    const defaultScope = role?.scope || allowedScopes[0] || 'Tenant';

    const [formData, setFormData] = useState({
        name: role?.name || '',
        scope: defaultScope
    });

    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput label="Role Name" id="name" value={formData.name} onChange={handleChange} required />
            <FormSelect label="Scope" id="scope" value={formData.scope} onChange={handleChange}>
                {allowedScopes.map(scope => (
                    <option key={scope} value={scope}>{scope}</option>
                ))}
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">{role ? 'Save Changes' : 'Create Role'}</Button>
            </div>
        </form>
    );
};

// --- Manage Roles Tab (Facelifted V1) ---
const ManageRolesTab = ({ token, roles, refreshRoles, currentUser, canAdminEditRole }) => {

    const [roleModal, setRoleModal] = useState({ isOpen: false, role: null });
    const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, item: null });

    const handleSaveRole = async (roleData) => {
        const isEditing = !!roleModal.role;
        const endpoint = isEditing ? `/roles/${roleModal.role.id}` : '/roles';
        const method = isEditing ? 'PUT' : 'POST';
        try {
            await apiFetch(endpoint, token, { method, body: JSON.stringify(roleData) });
            notify.success(`Role "${roleData.name}" saved.`);
            refreshRoles();
        } catch (err) { notify.success(err.message); }
        finally { setRoleModal({ isOpen: false, role: null }); }
    };

    const handleDelete = async () => {
        const { item } = confirmDelete;
        try {
            await apiFetch(`/roles/${item.id}`, token, { method: 'DELETE' });
            notify.success(`Role "${item.name}" deleted.`);
            refreshRoles();
        } catch (err) { notify.success(err.message); }
        finally { setConfirmDelete({ isOpen: false, item: null }); }
    };

    return (
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50 shrink-0">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Role Definitions</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Manage and configure access levels for your organization</p>
                </div>
                <Button onClick={() => setRoleModal({ isOpen: true, role: null })} className="flex items-center gap-2">
                    <DynamicIcon name="PlusCircle" className="w-4 h-4" />
                    <span>Create New Role</span>
                </Button>
            </div>
            
            <div className="flex-1 overflow-auto bg-white dark:bg-gray-900">
                <table className="min-w-max w-full text-left border-separate border-spacing-0">
                    <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/80 backdrop-blur-sm shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
                        <tr className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                            <th className="px-6 py-3 border-b border-gray-100 dark:border-gray-800 min-w-[300px]">Role Name</th>
                            <th className="px-6 py-3 border-b border-gray-100 dark:border-gray-800 min-w-[150px]">Scope</th>
                            <th className="px-6 py-3 border-b border-gray-100 dark:border-gray-800 text-right min-w-[120px]">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {roles?.map(role => (
                            <tr key={role.id} className="hover:bg-primary/[0.02] dark:hover:bg-primary/[0.05] transition-colors group">
                                <td className="px-6 py-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                            <DynamicIcon name="UserCog" className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{role.name}</p>
                                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">ID: {role.id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-3">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-tighter ${
                                        role.scope === 'Platform' 
                                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200/50' 
                                            : role.scope === 'Partner'
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200/50'
                                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200/50'
                                    }`}>
                                        {role.scope}
                                    </span>
                                </td>
                                <td className="px-6 py-3 text-right">
                                    <div className="flex justify-end gap-1">
                                        {canAdminEditRole(currentUser, role) ? (
                                            <>
                                                <button 
                                                    onClick={() => setRoleModal({ isOpen: true, role })}
                                                    title="Edit Role"
                                                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-md transition-all"
                                                >
                                                    <DynamicIcon name="Edit" className="w-3.5 h-3.5" />
                                                </button>
                                                <button 
                                                    onClick={() => setConfirmDelete({ isOpen: true, item: role })}
                                                    title="Delete Role"
                                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-all"
                                                >
                                                    <DynamicIcon name="Trash2" className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        ) : (
                                            <span className="text-[10px] text-gray-400 italic font-medium px-2 py-1">View Only</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={roleModal.isOpen} onClose={() => setRoleModal({ isOpen: false, role: null })}
                title={roleModal.role ? 'Edit Role' : 'Create New Role'}>
                <RoleForm role={roleModal.role} onSave={handleSaveRole} onCancel={() => setRoleModal({ isOpen: false, role: null })} currentUser={currentUser} />
            </Modal>

            <ConfirmModal isOpen={confirmDelete.isOpen} onClose={() => setConfirmDelete({ isOpen: false, item: null })}
                onConfirm={handleDelete} title="Delete Role">
                Are you sure you want to delete the "{confirmDelete.item?.name}" role? This cannot be undone and may affect users assigned to this role.
            </ConfirmModal>
        </div>
    );
};

// --- Main Page ---
const RolesPermissionsPage = ({ token, currentUser, refreshUser }) => {
    const isPlatformAdmin = ['r1', 'r2'].includes(currentUser?.roleId);

    // Partner filter — only applies to Platform Admins
    const [selectedPartnerId, setSelectedPartnerId] = useState('');

    // Build the roles URL dynamically based on filter selection:
    //   ''        → /roles           (PLATFORM roles only — default)
    //   '__all__' → /roles?partner_id=__all__  (every role across all owners)
    //   '<id>'    → /roles?partner_id=<id>     (ONLY that partner's clones)
    const rolesUrl = React.useMemo(() => {
        if (isPlatformAdmin && selectedPartnerId) {
            return `/roles?partner_id=${selectedPartnerId}`;
        }
        return '/roles';
    }, [isPlatformAdmin, selectedPartnerId]);

    const { data: initialRoles, isLoading: isLoadingRoles, refresh } = useData(rolesUrl, token);
    const { data: permissions, isLoading: isLoadingPerms } = useData('/roles/permissions', token);
    const { data: partners } = useData(isPlatformAdmin ? '/partners' : null, token);

    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('assignments');

    const canManageRoles = hasPermission(currentUser, PERMISSIONS.ROLES_MANAGE) || hasPermission(currentUser, 'roles:manage_definitions');

    const ROLE_ORDER = { r1: 1, r2: 2, r6: 3, r5: 4, r3: 5, r8: 6, r9: 7, r10: 8, r4: 9, r7: 10 };

    const sortedRoles = React.useMemo(() => {
        if (!initialRoles) return [];
        return [...initialRoles].sort((a, b) => {
            // PLATFORM roles come before PARTNER clones
            if (a.role_owner_type !== b.role_owner_type) {
                if (a.role_owner_type === 'PLATFORM') return -1;
                if (b.role_owner_type === 'PLATFORM') return 1;
            }
            const orderA = ROLE_ORDER[a.id?.toLowerCase()] ?? 99;
            const orderB = ROLE_ORDER[b.id?.toLowerCase()] ?? 99;
            return orderA - orderB || a.name.localeCompare(b.name);
        });
    }, [initialRoles]);

    const isPermissionInScope = (permission, roleScope, admin) => {
        if (!permission || !roleScope) return false;
        const adminRoleId = admin?.roleId?.toLowerCase?.() || '';
        const adminScope = admin?.roleScope || '';
        const scope = roleScope.toLowerCase();

        if ((adminScope === 'Tenant' || adminRoleId === 'r4') && scope !== 'tenant') return false;
        if ((adminScope === 'Partner' || adminRoleId === 'r3') && !['partner', 'tenant'].includes(scope)) return false;

        if (permission.is_system_only && !['r1', 'r2'].includes(adminRoleId)) return false;
        if (scope === 'tenant' && permission.is_tenant_scoped === false) return false;
        return true;
    };

    const canAdminEditRolePermission = (admin, targetRole) => {
        if (!admin || !targetRole) return false;
        const adminRoleId = admin.roleId?.toLowerCase?.() || '';
        
        // Super Admin can edit everything
        if (adminRoleId === 'r1') return true;
        
        // Platform Admin (r2) can edit any role except r1 and r2
        if (adminRoleId === 'r2') {
            const targetRoleId = (targetRole.cloned_from_role_id || targetRole.id)?.toLowerCase();
            return !['r1', 'r2'].includes(targetRoleId);
        }
        
        // Partner Admins (r3) and Tenant Admins (r4) can only edit roles owned by their own partner/tenant
        if (targetRole.role_owner_type === 'PLATFORM') return false;
        
        if (admin.tenantId && targetRole.owner_id === admin.tenantId) {
            if (adminRoleId === 'r3') return true;
            if (adminRoleId === 'r4' && targetRole.scope?.toLowerCase() === 'tenant') return true;
        }
        
        return false;
    };

    const canAdminEditRoleDefinition = (admin, targetRole) => {
        if (!admin || !targetRole) return false;
        if (!targetRole.is_editable) return false;
        return canAdminEditRolePermission(admin, targetRole);
    };

    const handleSaveBulk = async (updates) => {
        setIsSaving(true);
        try {
            const allVisiblePermissionIds = permissions.map(p => p.id);
            await apiFetch(`/roles/permissions/bulk`, token, {
                method: 'PUT',
                body: JSON.stringify({
                    updates,
                    provided_permission_ids: allVisiblePermissionIds.length > 0 ? allVisiblePermissionIds : null
                }),
            });
            notify.success("Changes saved successfully!");
            refresh(true);
            if (refreshUser) refreshUser();
            return true;
        } catch (err) {
            notify.error(`Error saving changes: ${err.message}`);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    if ((isLoadingRoles && !initialRoles) || (isLoadingPerms && !permissions) || !permissions || !initialRoles) return <LoadingSpinner />;

    // Derive a human-readable label for the active filter badge
    const filterLabel = selectedPartnerId === '__all__'
        ? 'All Roles'
        : partners?.find(p => p.id === selectedPartnerId)?.name ?? null;

    return (
        <StandardPageLayout>

            {/* Tab + Partner Filter Row */}
            <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">

                {/* Segmented Tab Switcher */}
                <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-900/60 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
                    <button
                        onClick={() => setActiveTab('assignments')}
                        className={`px-4 py-1.5 text-[13px] font-bold rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                            activeTab === 'assignments'
                                ? 'bg-white dark:bg-gray-700 text-gray-950 dark:text-white shadow-md border border-gray-200/30 dark:border-gray-600'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}
                    >
                        <span>Permissions</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-black transition-all duration-200 ${
                            activeTab === 'assignments'
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                                : 'bg-gray-200/50 dark:bg-gray-800/50 text-gray-500'
                        }`}>
                            {permissions?.length || 0}
                        </span>
                    </button>
                    {canManageRoles && (
                        <button
                            onClick={() => setActiveTab('manage')}
                            className={`px-4 py-1.5 text-[13px] font-bold rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                                activeTab === 'manage'
                                    ? 'bg-white dark:bg-gray-700 text-gray-950 dark:text-white shadow-md border border-gray-200/30 dark:border-gray-600'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                        >
                            <span>Manage Roles</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-black transition-all duration-200 ${
                                activeTab === 'manage'
                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                                    : 'bg-gray-200/50 dark:bg-gray-800/50 text-gray-500'
                            }`}>
                                {initialRoles?.length || 0}
                            </span>
                        </button>
                    )}
                </div>

                {/* Partner Filter — Platform Admin only */}
                {isPlatformAdmin && (
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="Building2" className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            Viewing:
                        </span>
                        <select
                            value={selectedPartnerId}
                            onChange={e => setSelectedPartnerId(e.target.value)}
                            className="text-[12px] font-semibold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-3 py-1.5 pr-8 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer appearance-none"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                        >
                            <option value="">Platform Roles Only</option>
                            <option value="__all__">All Roles</option>
                            {partners?.length > 0 && (
                                <optgroup label="─── Partners ───">
                                    {partners.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                        {/* Active filter badge */}
                        {filterLabel && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${
                                selectedPartnerId === '__all__'
                                    ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-200/60'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200/50'
                            }`}>
                                <DynamicIcon
                                    name={selectedPartnerId === '__all__' ? 'Layers' : 'Users'}
                                    className="w-3 h-3"
                                />
                                {filterLabel}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
                {activeTab === 'assignments' ? (
                    <RolesPermissionsMatrix
                        initialRoles={sortedRoles}
                        allPermissions={permissions}
                        onSave={handleSaveBulk}
                        isSaving={isSaving}
                        currentUser={currentUser}
                        canAdminEditRole={canAdminEditRolePermission}
                        isPermissionInScope={isPermissionInScope}
                    />
                ) : (
                    <ManageRolesTab
                        token={token}
                        roles={sortedRoles}
                        refreshRoles={refresh}
                        currentUser={currentUser}
                        canAdminEditRole={canAdminEditRoleDefinition}
                    />
                )}
            </div>
        </StandardPageLayout>
    );
};

export default RolesPermissionsPage;

